<script setup>
import { computed, ref } from 'vue';

import { usePlatform } from '../composables/usePlatform';
import { HrBtn, HrIcon, HrSkeleton } from '../primitives';
import HrAdaptiveMenu from './HrAdaptiveMenu.vue';

/**
 * The title of what is on screen, which is also the control for changing it.
 *
 * The name of what is being shown, pressable, opening a menu of the other things it could show.
 *
 * **It is a button wearing the title's type, not a title that happens to be clickable.** A name in
 * headline type with a small caret beside it reads as a heading, and readers do not press headings;
 * that is what user testing found. So the control takes a button's whole vocabulary — a resting
 * container, state layers, a focus ring, one press action — and the headline role is only the type
 * it is set in. No other button reaches headline type, and none should: their type roles are label
 * roles, sized for a control among controls rather than for the thing a page is about.
 *
 * **The component owns the menu, the consumer owns what is in it.** Opening, closing, anchoring,
 * the desktop-or-bottom-sheet decision, `aria-expanded` and returning focus to the trigger are the
 * parts that drift page by page when each caller wires up its own menu, so they are settled here
 * once. Everything inside the panel arrives through the `menu` slot, which is what keeps this
 * component free of any knowledge of saved views, caseloads, or whatever else is being switched.
 *
 * **It translates nothing.** The label, the marker, the accessible name and the sheet's title all
 * arrive composed. Joining a name to its marker into "Supervised clients, edited" is a sentence,
 * and sentences are the caller's to build in its own language.
 *
 * @status recommended
 * @group actions
 * @useWhen The title of what is on screen is also how the reader switches to a different version of it
 * @avoidWhen There is nothing to switch to — render a plain title, rather than offering a menu that opens on nothing
 * @avoidWhen The control is one among several in a toolbar — use HrDropdownMenu or HrIconMenuBtn
 * @related HrAdaptiveMenu
 * @related HrIconMenuBtn
 * @a11y One native button carrying `aria-haspopup` and `aria-expanded`, and nothing interactive
 * inside it. `aria-haspopup` follows the surface rather than being fixed: the desktop panel is a
 * menu and the compact one is a bottom sheet, which is a dialog. The marker is part of the
 * accessible name and never a live region, because what it marks can change on every keystroke in
 * a search field. Navigation between the items in the panel is whatever those items bring; this
 * component adds none.
 */
defineOptions({ name: 'HrTitleMenuBtn', inheritAttrs: false });

defineProps({
  /**
   * The title, set in headline type. Truncates with an ellipsis rather than pushing the controls
   * beside it off the row; the untruncated text stays in the accessible name.
   * @example Supervised clients
   */
  label: {
    type: String,
    required: true,
  },
  /**
   * A short qualifier on what is currently shown — "Edited", "Draft" — rendered after the label as
   * a dot and a word. Non-interactive, and absent when null.
   *
   * The component has no opinion on what qualifies: it renders the caller's word. Whatever it says
   * belongs in `accessibleName` too, since the marker itself is not announced.
   * @example Edited
   */
  markerLabel: {
    type: String,
    default: null,
  },
  /**
   * The button's accessible name, composed — "Supervised clients, edited". Falls back to `label`.
   *
   * Separate from `label` because the visible text truncates and the marker is a glyph beside it,
   * so what is on screen is not what should be read out.
   */
  accessibleName: {
    type: String,
    default: null,
  },
  /**
   * Header for the bottom sheet the menu becomes on a compact screen. Not rendered on desktop,
   * where the panel is anchored to the trigger and needs no title to say what it belongs to.
   * @example Saved views
   */
  menuTitle: {
    type: String,
    default: null,
  },
  /**
   * Which of the things this could show is on screen is not known yet, so the title holds its place
   * instead of naming one. The control cannot be pressed while it waits: the menu lists what has
   * not been read, and the name beside it would be answering a question still being asked.
   *
   * For the name alone, not for the contents of the menu. A menu that loads when it opens is the
   * normal case and says nothing about the title, which already names what is on screen.
   */
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Fired when the menu opens. Use it to load what the menu lists, rather than loading it up front. */
  show: null,
  /** Fired when the menu closes. */
  hide: null,
});

const isOpen = ref(false);

/*
 * The panel is a menu on a pointer and a bottom sheet on a touch screen, and a bottom sheet is a
 * dialog. Announcing "menu" on both would describe a surface half of the readers never get.
 */
const popupRole = computed(() => (usePlatform().is.mobile ? 'dialog' : 'menu'));

const setOpen = (value) => {
  if (isOpen.value === value) {
    return;
  }
  isOpen.value = value;
  emit(value ? 'show' : 'hide');
};

const menuRef = ref(null);

defineExpose({
  /** Opens the menu. */
  show: () => setOpen(true),
  /** Closes the menu. */
  hide: () => setOpen(false),
  /**
   * Re-measures the panel and places it again, for contents that change size while it is open —
   * rows arriving after a fetch, for instance, which the panel was not sized for when it opened.
   */
  updatePosition: () => menuRef.value?.updatePosition(),
});

/*
 * The design-system theme paints the resting container; the legacy theme has no tonal role that
 * suits a title (its secondary is red), so it keeps the flat button it has always had.
 * @todo Remove legacy-props when the Legacy theme is removed.
 */
const LEGACY_TRIGGER_PROPS = { flat: true, color: 'dark', 'no-caps': true };
</script>

<template>
  <div class="hr-title-menu-btn">
    <!--
      Attributes are routed here rather than inherited: the root is a positioning shell the panel
      anchors to, and a test hook or an aria attribute left on it would describe a div nobody can
      press. They go on first, so the open state and the popup's kind stay the component's to
      report rather than a caller's to set.
    -->
    <hr-btn
      v-bind="$attrs"
      variant="tonal"
      class="hr-title-menu-btn__trigger"
      :class="{ 'hr-title-menu-btn__trigger--expanded': isOpen }"
      :aria-haspopup="popupRole"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-label="accessibleName ?? label"
      :disable="loading"
      :aria-busy="loading ? 'true' : undefined"
      :legacy-props="LEGACY_TRIGGER_PROPS"
      @click="setOpen(!isOpen)"
    >
      <hr-skeleton
        v-if="loading"
        class="hr-title-menu-btn__label-placeholder"
        type="text"
      />
      <span
        v-else
        class="hr-title-menu-btn__label"
      >{{ label }}</span>
      <span
        v-if="markerLabel !== null && !loading"
        class="hr-title-menu-btn__marker"
      >
        <span class="hr-title-menu-btn__marker-dot" />
        <span class="hr-title-menu-btn__marker-label">{{ markerLabel }}</span>
      </span>
      <span class="hr-title-menu-btn__chevron">
        <hr-icon :name="isOpen ? 'expand_less' : 'expand_more'" />
      </span>
    </hr-btn>

    <!--
      A sibling of the button rather than a child of it, so the panel anchors to the whole trigger
      box: q-menu measures against its parent element, and inside the button that parent is the
      content row rather than the button itself.
    -->
    <hr-adaptive-menu
      ref="menuRef"
      :model-value="isOpen"
      :title="menuTitle"
      anchor="bottom start"
      self="top start"
      @update:model-value="setOpen"
    >
      <!-- @slot The panel's contents. Rows, headers and dividers are the caller's. -->
      <slot name="menu" />
    </hr-adaptive-menu>
  </div>
</template>

<style lang="scss">

/*
 * Not scoped, and matched on the component's own classes. Two reasons the scoped form cannot do
 * this job: the rules have to reach q-btn's content row, which is Quasar's element rather than
 * ours, and the themed rules pair [data-theme] with a class on the same element, which Vue rewrites
 * into a selector wanting the theme attribute and the scope id together — one sits on <html> and
 * the other on the button, so it matches nothing.
 */

.hr-title-menu-btn {
  position: relative;
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
}

/*
 * Height and the leading gap hold in both themes: the target is 48dp wherever it renders, and the
 * gap belongs to the content rather than to the palette.
 */
.hr-title-menu-btn .hr-title-menu-btn__trigger {
  min-height: var(--app-comp-title-menu-btn-container-height);
  max-width: 100%;
}

.hr-title-menu-btn__trigger .q-btn__content {
  min-width: 0;
  flex-wrap: nowrap;
  gap: var(--app-comp-title-menu-btn-content-gap);
}

/*
 * The width a name of ordinary length takes, so the row is the size it will be once the name lands
 * and the controls beside it do not move when it does.
 */
.hr-title-menu-btn__label-placeholder {
  width: var(--app-comp-title-menu-btn-placeholder-width);
  height: var(--app-type-headline-small-size);
}

/* The label is the only part that gives way, so the marker and the chevron are never what clips. */
.hr-title-menu-btn__label {
  min-width: 0;
  overflow: hidden;
  font-size:      var(--app-type-headline-small-size);
  line-height:    var(--app-type-headline-small-line-height);
  font-weight:    var(--app-type-headline-small-weight);
  letter-spacing: var(--app-type-headline-small-tracking);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/*
 * A word in the container's own on-color, not a pill in an accent of its own. The dot carries the
 * tint; the text carrying it too made a third accent inside a container that is already beside the
 * page's primary action, and the marker started competing instead of reporting.
 */
.hr-title-menu-btn__marker {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: var(--app-comp-title-menu-btn-marker-gap);
  color: inherit;
  font-size:      var(--app-type-label-medium-size);
  line-height:    var(--app-type-label-medium-line-height);
  font-weight:    var(--app-type-label-medium-weight);
  letter-spacing: var(--app-type-label-medium-tracking);
  white-space: nowrap;
}

/*
 * Under this the word goes and the dot stays, which buys the row back the width the word took
 * before anything else has to give. Measured against the space the trigger is in rather than the
 * window, so an open navigation drawer counts. A literal because a container query cannot read a
 * custom property, and 520px is where a name of ordinary length, its marker and the actions beside
 * it stop fitting on one line.
 *
 * Nothing is lost to a screen reader: the marker is not part of the accessible name in the first
 * place — `accessibleName` carries the state in words, and does so whether or not the word shows.
 */
@container (max-width: 520px) {
  .hr-title-menu-btn__marker-label {
    display: none;
  }
}

.hr-title-menu-btn__marker-dot {
  width: var(--app-comp-title-menu-btn-marker-dot-size);
  height: var(--app-comp-title-menu-btn-marker-dot-size);
  border-radius: var(--app-shape-corner-full);
  background: var(--app-primary);
}

/*
 * No fill and no edge of its own: the container is what says the trigger is pressable, so a circle
 * drawn inside it only divides one control into what looks like two — the anatomy of the split
 * button sitting further along the same row. The box is kept for the rhythm it gives the row.
 */
.hr-title-menu-btn__chevron {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: var(--app-comp-title-menu-btn-chevron-size);
  height: var(--app-comp-title-menu-btn-chevron-size);
  color: var(--app-on-surface-variant);
}

/*
 * MD2 gets the same structure, built from what MD2 has. Its surface-container roles all resolve to
 * pure white and it has no tonal role at all, so the container is a low-opacity on-surface overlay
 * instead — the fill MD2 puts under its own filled components.
 *
 * The corner radius is deliberately left to Quasar. Its button radius is what every other MD2
 * control on the row already uses, and the shape tokens are not defined outside a theme, so
 * reaching for one here would silently resolve to nothing.
 */
html:not([data-theme]) .hr-title-menu-btn__trigger {
  background: var(--app-comp-title-menu-btn-legacy-container);
}

/*
 * Hover, pressed and the ripple come from Quasar's own focus helper, which stays live here because
 * HrBtn only silences it under a theme. The ring is the one addition: the helper's overlay is a
 * wash on a fill this light, and a focus indicator that can be missed is not one.
 */
html:not([data-theme]) .hr-title-menu-btn__trigger:focus-visible {
  outline: 2px solid var(--app-primary);
  outline-offset: 2px;
}

/*
 * Three classes deep on purpose. HrBtn's own rules carry a scope attribute, which lifts them to the
 * weight of two classes, so a lighter selector here would be settled by source order instead.
 */
/*
 * Neutral rather than tonal. The accent containers belong to the filters, and a view is what holds
 * a set of filters rather than one more of them — sharing their role made the two read as siblings.
 * A surface step also keeps the trigger under the page's primary action, which a saturated fill on
 * the largest text on the page does not.
 */
[data-theme] .hr-title-menu-btn__trigger.hr-btn {
  min-height: var(--app-comp-title-menu-btn-container-height);
  padding-block: 0;
  padding-inline:
    var(--app-comp-title-menu-btn-padding-start)
    var(--app-comp-title-menu-btn-padding-end);
  border-radius: var(--app-shape-corner-medium);
  /* Reserved at rest, so opening the menu cannot change the button's box and shift the row. */
  border: 1px solid transparent;
  background: var(--app-surface-container);
  color: var(--app-on-surface);
}

/* The state layer follows the container it sits on, rather than the tonal role HrBtn assumes. */
[data-theme] .hr-title-menu-btn__trigger.hr-btn::before {
  background: var(--app-on-surface);
}

/* Quiet at rest, and a step firmer under the pointer — the emphasis arrives when it is asked for. */
[data-theme] .hr-title-menu-btn__trigger.hr-btn:hover {
  background: var(--app-surface-container-high);
}

/*
 * The one accent, and only while the panel is open: it is what ties the trigger to the surface
 * hanging off it. After :hover in source order, since both weigh the same.
 */
[data-theme] .hr-title-menu-btn__trigger.hr-btn.hr-title-menu-btn__trigger--expanded {
  border-color: var(--app-primary);
  background: var(--app-surface-container-high);
}

</style>
